import { useTranslation } from '@bcba/react-hooks';
import {
  Box,
  FiSearch,
  FormProvider,
  Modal,
  Table,
  TableInstance,
  TableWrapper,
  TextField,
  useTheme,
  FiDownload,
  Flex,
  Button,
} from '@agrotoken-tech/ui';
import { Pagination } from '@bcba/frontend-utils';
import { useReceivedDigitalDossierAccessRequestsTable } from './useReceivedAccessRequests';
import { AccessibleSectionCheckboxGroup } from './AccessibleSectionCheckboxGroup';
import { MultiSelectDropdown } from '../../MultiSelectDropdown/MultiSelectDropdown';

export const ReceivedAccessRequestsTable = () => {
  const { t } = useTranslation();

  const {
    data,
    formMethods,
    isLoading,
    setPage,
    tableInstance,
    selectedCompanyName,
    isOpenApproveModal,
    onCloseApproveModal,
    approvesReceivedAccessRequest,
    isPendingApproveRequest,
    isOpenRejectModal,
    onCloseRejectModal,
    rejectsReceivedAccessRequest,
    isPendingRejectRequest,
    isOpenRevokeModal,
    onCloseRevokeModal,
    revokesReceivedAccessRequest,
    isPendingRevokeRequest,
    isOpenAccessibleSectionModal,
    onCloseAccessibleSectionModal,
    accessibleSections,
    handleChangeAccessibleSections,
    updateAccessibleSections,
    isLoadingMutateAccessibleSection,
    selectedStatuses,
    statusOptions,
    onFiltersReset,
    setSelectedStatuses,
  } = useReceivedDigitalDossierAccessRequestsTable();

  const theme = useTheme();

  return (
    <>
      <FormProvider {...formMethods}>
        <TableWrapper
          searchComponent={
            <TextField
              name="search"
              leftElement={<FiSearch />}
              placeholder={t('common.table.search.placeholder')}
            />
          }
          quickFilters={
            <Flex gap={4} alignItems={'center'}>
              <MultiSelectDropdown
                options={statusOptions}
                placeholder={t(
                  'backoffice.pages.digitalDossiers.table.statusFilter.placeholder',
                )}
                selectedOptions={selectedStatuses}
                setSelectedOptions={setSelectedStatuses}
                allText={t(
                  'backoffice.pages.digitalDossiers.table.statusFilter.allText',
                )}
              />
              <Button variant={'linkPrimary'} onClick={onFiltersReset}>
                {t('common.reset')}
              </Button>
            </Flex>
          }
        >
          <Table
            tableInstance={tableInstance as TableInstance<object>}
            isLoading={isLoading}
            emptyStateContent={{
              title: t('common.noResults'),
              description: t('common.table.emptyState'),
            }}
          ></Table>
        </TableWrapper>
        <Box my="3">
          <Pagination
            currentPage={data?.paginationData.currentPage!}
            totalPageCount={data?.paginationData.totalPages!}
            previousButtonLabel={t('common.previous')}
            nextButtonLabel={t('common.next')}
            onPageChange={setPage}
            theme={theme}
          />
        </Box>
      </FormProvider>

      <Modal
        isOpen={isOpenApproveModal}
        title={t('receivedAccessRequests.approveRequest.modal.title')}
        text={t('receivedAccessRequests.approveRequest.modal.text')}
        onClose={onCloseApproveModal}
        onClickConfirm={approvesReceivedAccessRequest}
        icon={FiDownload}
        labelBtnConfirm={t('common.approve')}
        labelBtnClose={t('common.cancel')}
        isLoadingConfirmBtn={isPendingApproveRequest}
      >
        <AccessibleSectionCheckboxGroup
          accessibleSections={accessibleSections}
          handleChangeAccessibleSections={handleChangeAccessibleSections}
        />
      </Modal>

      <Modal
        isOpen={isOpenRejectModal}
        title={t('receivedAccessRequests.rejectRequest.modal.title').replace(
          '{{selectedCompanyName}}',
          selectedCompanyName ?? '',
        )}
        onClose={onCloseRejectModal}
        onClickConfirm={rejectsReceivedAccessRequest}
        labelBtnConfirm={t('common.reject')}
        labelBtnClose={t('common.cancel')}
        isLoadingConfirmBtn={isPendingRejectRequest}
      />

      <Modal
        isOpen={isOpenRevokeModal}
        title={t('receivedAccessRequests.revokeRequest.modal.title').replace(
          '{{selectedCompanyName}}',
          selectedCompanyName ?? '',
        )}
        onClose={onCloseRevokeModal}
        onClickConfirm={revokesReceivedAccessRequest}
        labelBtnConfirm={t('common.revokeAccess')}
        labelBtnClose={t('common.cancel')}
        isLoadingConfirmBtn={isPendingRevokeRequest}
      />

      <Modal
        isOpen={isOpenAccessibleSectionModal}
        icon={FiDownload}
        title={t('receivedAccessRequests.accessibleSections.modal.title')}
        text={t('receivedAccessRequests.accessibleSections.modal.description')}
        onClose={onCloseAccessibleSectionModal}
        onClickConfirm={updateAccessibleSections}
        labelBtnConfirm={t('common.save')}
        labelBtnClose={t('common.cancel')}
        isLoadingConfirmBtn={isLoadingMutateAccessibleSection}
      >
        <AccessibleSectionCheckboxGroup
          accessibleSections={accessibleSections}
          handleChangeAccessibleSections={handleChangeAccessibleSections}
        />
      </Modal>
    </>
  );
};
